import { EntityTypeName, IBackgroundJobEntity } from "@odata/GeneratedEntityTypes";
import { BackgroundJobTypeCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IBackgroundJobResults } from "@pages/backgroundJob/BackgroundJob.utils";
import { isDefined, isNotDefined } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { IconSize, Status } from "../../enums";
import { CloseIcon, FieldValidationPositiveIcon, FieldValidationWarningIcon } from "../icon";
import { TProgressBarDescFormatter } from "./ProgressBar";
import { StatusDescriptionFormatterWrapper } from "./ProgressBar.styles";

export enum ProgressBarColor {
    Default = "Default",
    Semantic = "Semantic",
    Gray = "Gray"
}

const progressStatusFormatter = (status: Status, value: number, parts: number): React.ReactElement => {
    const Icon = status === Status.Success ? FieldValidationPositiveIcon : status === Status.Warning ? FieldValidationWarningIcon : CloseIcon;

    return (
        <StatusDescriptionFormatterWrapper>
            <Icon width={IconSize.XS}
                  color={status === Status.Error ? "C_SEM_el_bad" : null}
                  preventHover/>
            {i18next.t(`Components:ProgressBar.Description${status}`)}
        </StatusDescriptionFormatterWrapper>
    );
};

export const progressSuccessFormatter: TProgressBarDescFormatter = (value: number, parts: number): React.ReactElement => {
    return progressStatusFormatter(Status.Success, value, parts);
};

export const progressSuccessWithErrorFormatter: TProgressBarDescFormatter = (value: number, parts: number): React.ReactElement => {
    return (
        <StatusDescriptionFormatterWrapper>
            <FieldValidationWarningIcon width={IconSize.XS} preventHover/>
            {i18next.t(`Components:ProgressBar.SuccessWithErrors`)}
        </StatusDescriptionFormatterWrapper>
    );
};

export const progressWarningFormatter: TProgressBarDescFormatter = (value: number, parts: number): React.ReactElement => {
    return progressStatusFormatter(Status.Warning, value, parts);
};

export const progressErrorFormatter: TProgressBarDescFormatter = (value: number, parts: number): React.ReactElement => {
    return progressStatusFormatter(Status.Error, value, parts);
};

export interface IPostMultipleDraftsParameters {
    DraftIds: number[];
    DocumentTypeCode: DocumentTypeCode;
    IsTaxProformaDocument?: boolean;
}

interface IJobResult extends IBackgroundJobResults {
    FailedItemsCount: number;
    TotalItemsCount: number;
}

interface IErrorResults {
    errorCount: number;
    totalCount: number;
}

const getFailedItemsCount = (job: IBackgroundJobEntity): IErrorResults => {
    try {

        const parsedResult = JSON.parse(job.Result) as Partial<IJobResult>;

        if (job.IsResultReadable) {
            const errorCount = parsedResult?.ItemsFailed.length ?? null;
            const totalCount = (parsedResult?.ItemsChanged?.length ?? 0) + (parsedResult?.ItemsSkipped?.length ?? 0) + (errorCount ?? 0);
            return { errorCount, totalCount };
        }

        return {
            errorCount: typeof parsedResult?.FailedItemsCount === "number" ? parsedResult.FailedItemsCount : null,
            totalCount: null
        };
    } catch {
        return null;
    }
};

export const getProgressFinishedWithWarningFormatter = (job: IBackgroundJobEntity): TProgressBarDescFormatter => {
    const data = getFailedItemsCount(job);

    if (isNotDefined(data?.errorCount)) {
        return progressSuccessWithErrorFormatter;
    }

    const text = isDefined(data.totalCount) ? `${data.errorCount}/${data.totalCount}` : `${data.errorCount}`;

    return (value: number, parts: number): React.ReactElement => {
        return (
            <StatusDescriptionFormatterWrapper>
                <FieldValidationWarningIcon width={IconSize.XS}
                                            preventHover/>
                {`${i18next.t("Components:ProgressBar.JobError")}: ${text}`}
            </StatusDescriptionFormatterWrapper>
        );
    };
};

export const getBackgroundJobTitle = (job: IBackgroundJobEntity): string => {
    if (job.TypeCode === BackgroundJobTypeCode.PostMultipleDrafts && job.Parameters) {
        try {
            const parameters = JSON.parse(job.Parameters) as IPostMultipleDraftsParameters;
            const isDdop = parameters.IsTaxProformaDocument && [DocumentTypeCode.ProformaInvoiceReceived, DocumentTypeCode.ProformaInvoiceIssued].includes(parameters.DocumentTypeCode);
            const key = `BackgroundJobs:PostMultipleDrafts.Title${parameters.DocumentTypeCode}${isDdop ? "DDOP" : ""}`;

            return i18next.exists(key) ? i18next.t(key) : i18next.t("BackgroundJobs:PostMultipleDrafts.Title");
        } catch (e) {
            return i18next.t("BackgroundJobs:PostMultipleDrafts.Title");
        }
    }

    return i18next.t(`${getEnumNameSpaceName(EntityTypeName.BackgroundJobType)}:${job.TypeCode}`);
};
